/**
 * IndexedDB cache for the dataset bundle, keyed on the bundle's version stamp.
 *
 * The bundle is ~296 KB gzipped, so the win here is not bandwidth — it is skipping
 * parse-and-transfer on repeat visits. A stale entry is dropped whenever the server
 * reports a different version, so there is no invalidation logic to get wrong.
 */
import type { Dataset } from './types'
import { viewAsKey } from './viewAs'

const DB = 'cost-per-claim'
const STORE = 'dataset'
/**
 * The caller's own bundle, or — while viewing as someone — that person's, under its
 * own key. One slot for both would let a reload show the other view's data before it
 * revalidated.
 */
const KEY = viewAsKey('bundle')
/** The row-level artefacts Claim detail and Touch detail read, by artefact name. */
const DETAIL = 'detail'

function open(): Promise<IDBDatabase> {
  return new Promise((res, rej) => {
    // Version 2 added the detail store. An upgrade only ever creates missing stores,
    // so a browser holding a version-1 bundle keeps it.
    const req = indexedDB.open(DB, 2)
    req.onupgradeneeded = () => {
      for (const name of [STORE, DETAIL]) {
        if (!req.result.objectStoreNames.contains(name)) req.result.createObjectStore(name)
      }
    }
    req.onsuccess = () => res(req.result)
    req.onerror = () => rej(req.error)
  })
}

interface Entry {
  version: string
  /**
   * The server's ETag for this exact response.
   *
   * Stored separately from `version` because they are not the same thing: the version
   * identifies the BUILD, the ETag identifies the build as scoped for THIS caller. A
   * cache that revalidates on the version alone keeps serving the slice a user had
   * before their permissions changed, and no new grant can ever take effect.
   */
  etag: string
  dataset: Dataset
}

/** Never throws — a browser with IndexedDB blocked simply gets a cache miss. */
export async function read(): Promise<Entry | null> {
  try {
    const db = await open()
    return await new Promise((res, rej) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(KEY)
      req.onsuccess = () => res((req.result as Entry) ?? null)
      req.onerror = () => rej(req.error)
    })
  } catch {
    return null
  }
}

export async function write(dataset: Dataset, etag: string): Promise<void> {
  try {
    const db = await open()
    await new Promise<void>((res, rej) => {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).put(
        { version: dataset.version, etag, dataset } satisfies Entry,
        KEY,
      )
      tx.oncomplete = () => res()
      tx.onerror = () => rej(tx.error)
    })
  } catch {
    /* cache is an optimisation; failing to write it is not an error */
  }
}

/** A cached detail artefact and the ETag it was served under. */
export interface DetailEntry<T> {
  etag: string
  data: T
}

/** Never throws — blocked or missing IndexedDB is a cache miss. */
export async function readDetail<T>(name: string): Promise<DetailEntry<T> | null> {
  try {
    const db = await open()
    return await new Promise((res, rej) => {
      const req = db.transaction(DETAIL, 'readonly').objectStore(DETAIL).get(name)
      req.onsuccess = () => res((req.result as DetailEntry<T>) ?? null)
      req.onerror = () => rej(req.error)
    })
  } catch {
    return null
  }
}

export async function writeDetail<T>(name: string, etag: string, data: T): Promise<void> {
  try {
    const db = await open()
    await new Promise<void>((res, rej) => {
      const tx = db.transaction(DETAIL, 'readwrite')
      tx.objectStore(DETAIL).put({ etag, data } satisfies DetailEntry<T>, name)
      tx.oncomplete = () => res()
      tx.onerror = () => rej(tx.error)
    })
  } catch {
    /* cache is an optimisation; failing to write it (quota, private mode) is not an error */
  }
}

/**
 * Everything this app stored: the bundle (named payroll) and the detail rows (staff
 * notes that can name patients). Called on sign-out, so a shared machine does not keep
 * the last user's data at rest after they leave.
 */
export async function clear(): Promise<void> {
  try {
    const db = await open()
    await new Promise<void>((res, rej) => {
      const tx = db.transaction([STORE, DETAIL], 'readwrite')
      tx.objectStore(STORE).clear()
      tx.objectStore(DETAIL).clear()
      tx.oncomplete = () => res()
      tx.onerror = () => rej(tx.error)
    })
  } catch {
    /* ignore */
  }
}
